import { supabase } from "@/integrations/supabase/client";

/**
 * Utility functions for handling phone numbers
 */

/**
 * Normalizes a phone number to a consistent format with country code
 * Handles cases where users might enter country code multiple times or in different formats
 * 
 * @param phoneNumber The phone number to normalize
 * @param defaultCountryCode The default country code to use if none is detected (e.g., "+254")
 * @returns The normalized phone number in E.164 format
 */
export const normalizePhoneNumber = (phoneNumber: string, defaultCountryCode: string = "+254"): string => {
  // Remove all non-digit characters except '+'
  let cleaned = phoneNumber.replace(/[^\d+]/g, "");
  
  // If the number doesn't start with +, add the default country code
  if (!cleaned.startsWith("+")) {
    // Check if it starts with leading zeros and remove them
    cleaned = cleaned.replace(/^0+/, "");
    
    // Check if the number already starts with the country code without the +
    // For example, if phone is "254722123456" and default code is "+254"
    const countryCodeWithoutPlus = defaultCountryCode.substring(1); // "254"
    if (cleaned.startsWith(countryCodeWithoutPlus)) {
      cleaned = "+" + cleaned;
    } else {
      // Otherwise, add the default country code
      cleaned = defaultCountryCode + cleaned;
    }
  } else {
    // The number starts with +, now check for duplicated country codes
    // E.g., +254254722123456 where 254 is duplicated
    
    // Get all country codes we recognize
    const countryCodes = [
      "+254", // Kenya
      "+256", // Uganda
      "+255", // Tanzania
      "+251", // Ethiopia
      "+250", // Rwanda
      "+1", // US/Canada
      "+44", // UK
      "+91", // India
      "+234", // Nigeria
      "+27", // South Africa
      "+971", // UAE
      "+61", // Australia
      "+49", // Germany
      "+33", // France
      "+86", // China
      "+55", // Brazil
      "+52", // Mexico
      "+81", // Japan
    ];
    
    // Sort by length descending to match longer codes first
    const sortedCodes = countryCodes.sort((a, b) => b.length - a.length);
    
    // Find the country code at the start of the number
    const matchedCode = sortedCodes.find(code => cleaned.startsWith(code));
    
    if (matchedCode) {
      // Remove the plus sign to work with just digits
      const withoutPlus = cleaned.substring(1);
      const codeWithoutPlus = matchedCode.substring(1);
      
      // Check if the country code appears twice
      // For example: +254254... -> the codeWithoutPlus is "254" and we check if after the "+" 
      // there's another "254" causing duplication
      if (withoutPlus.startsWith(codeWithoutPlus + codeWithoutPlus)) {
        // Remove the second occurrence of the country code
        // +254254123456 becomes +254123456
        cleaned = matchedCode + withoutPlus.substring(codeWithoutPlus.length);
      }
      
      // Also check if the number has country code appended in a different format
      // For example: +2540254123456 -> country code is followed by a 0 and then the code again
      const escapedMatchedCode = matchedCode.replace(/\+/g, "\\+");
      const alternatePattern = new RegExp(`^${escapedMatchedCode}0+${codeWithoutPlus}`);
      if (alternatePattern.test(cleaned)) {
        // Remove the duplicated pattern after the initial country code and zero
        cleaned = cleaned.replace(alternatePattern, matchedCode);
      }
    }
  }
  
  return cleaned;
};

/**
 * Checks if a phone number exists in the database
 * Uses the normalized format for comparison to prevent duplicates
 * 
 * @param phoneNumber The phone number to check
 * @returns Promise<boolean> True if the phone number exists, false otherwise
 */
export const phoneNumberExists = async (phoneNumber: string): Promise<boolean> => {
  try {
    // Normalize the phone number first
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id')
      .or(`whatsapp_number.eq.${normalizedPhone},whatsapp_number.eq.${phoneNumber}`)
      .limit(1);
    
    if (profilesError) {
      console.error("Error checking if phone number exists in profiles:", profilesError);
      return false;
    }
    
    return profiles && profiles.length > 0;
  } catch (error) {
    console.error("Error checking if phone number exists:", error);
    return false;
  }
};

/**
 * Formats a phone number for display
 * 
 * @param phoneNumber The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneForDisplay = (phoneNumber: string): string => {
  const normalized = normalizePhoneNumber(phoneNumber);
  
  // Format phone number for display based on country code
  // This is a simple implementation - expand based on requirements
  if (normalized.startsWith("+254")) {
    // Kenya format: +254 7XX XXX XXX
    const local = normalized.substring(4);
    if (local.length === 9) {
      return `+254 ${local.substring(0, 3)} ${local.substring(3, 6)} ${local.substring(6)}`;
    }
  }
  
  // Default formatting with spaces every 3 digits after country code
  return normalized.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{3,4})/, "$1 $2 $3 $4");
}; 